import React, { useEffect, useState } from "react";
import { api, extractError } from "../lib/api";
import { Pill } from "./Ui";
import { formatListDate } from "../lib/purchaseListMeta";

function fmtQtl(v) {
  if (v == null || v === "") return "—";
  return Number(v).toFixed(2);
}

function stateTone(state) {
  if (state === "COMPLETED") return "sage";
  if (state === "ACCOUNTS_PENDING") return "gold";
  return "muted";
}

export default function PurchaseContractFulfillmentPanel({ contractId }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!contractId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .get(`/purchase-contracts/${contractId}/fulfillment`)
      .then((r) => setData(r.data))
      .catch((err) => setError(extractError(err)))
      .finally(() => setLoading(false));
  }, [contractId]);

  if (!contractId) return null;

  const summary = data?.summary;
  const purchases = data?.purchases || [];

  return (
    <section className="mt-6 border-t pt-5 space-y-4" data-testid="contract-fulfillment-panel">
      <div>
        <h3 className="text-sm font-semibold">Truck fulfillment</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Purchases linked to this contract — received, reserved, and unloading detail.
        </p>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Loading fulfillment…</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}

      {!loading && summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-xs">
          <SummaryCell label="Ordered" value={`${fmtQtl(summary.quantity_qtl)} qtl`} />
          <SummaryCell label="Received" value={`${fmtQtl(summary.received_qtl)} qtl`} />
          <SummaryCell label="Reserved" value={`${fmtQtl(summary.reserved_qtl)} qtl`} />
          <SummaryCell label="Balance" value={`${fmtQtl(summary.balance_qtl)} qtl`} highlight />
          {summary.quantity_limit_qtl != null && (
            <SummaryCell
              label={`Max (±${summary.quantity_tolerance_pct ?? 10}%)`}
              value={`${fmtQtl(summary.quantity_limit_qtl)} qtl`}
            />
          )}
          {summary.fulfillment_headroom_qtl != null && (
            <SummaryCell label="Headroom" value={`${fmtQtl(summary.fulfillment_headroom_qtl)} qtl`} />
          )}
        </div>
      )}

      {!loading && data && purchases.length === 0 && (
        <p className="text-sm text-muted-foreground">No trucks linked to this contract yet.</p>
      )}

      {!loading && purchases.length > 0 && (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full min-w-[720px] text-xs border-collapse">
            <thead>
              <tr className="border-b text-muted-foreground text-left">
                <th className="py-2 pr-3 font-medium">Purchase</th>
                <th className="py-2 pr-3 font-medium">Date</th>
                <th className="py-2 pr-3 font-medium">Vehicle</th>
                <th className="py-2 pr-3 font-medium">State</th>
                <th className="py-2 pr-3 font-medium text-right">Party net</th>
                <th className="py-2 pr-3 font-medium text-right">Truck net</th>
                <th className="py-2 pr-3 font-medium text-right">Billable</th>
                <th className="py-2 pr-3 font-medium text-right">Bags</th>
                <th className="py-2 font-medium">Unloading</th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((row) => (
                <tr key={row.purchase_id} className="border-b border-border/60 align-top">
                  <td className="py-2.5 pr-3 font-mono">{row.purchase_code}</td>
                  <td className="py-2.5 pr-3 whitespace-nowrap">
                    {formatListDate(row.gate_pass_date)}
                  </td>
                  <td className="py-2.5 pr-3">{row.vehicle_number || "—"}</td>
                  <td className="py-2.5 pr-3">
                    <Pill tone={stateTone(row.state)}>{row.state?.replace(/_/g, " ")}</Pill>
                    {row.is_open && row.reserved_qtl != null && (
                      <span className="block text-[10px] text-muted-foreground mt-0.5">
                        {fmtQtl(row.reserved_qtl)} qtl reserved
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 pr-3 text-right tabular-nums">{fmtQtl(row.party_net_weight_qtl)}</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums">
                    {fmtQtl(row.allocation_net_weight_qtl ?? row.net_weight_qtl)}
                  </td>
                  <td className="py-2.5 pr-3 text-right tabular-nums font-medium">
                    {fmtQtl(row.billable_qtl)}
                  </td>
                  <td className="py-2.5 pr-3 text-right tabular-nums">
                    {row.bags_unloaded ?? row.bags_reported ?? "—"}
                  </td>
                  <td className="py-2.5">
                    {row.unloading ? (
                      <div className="space-y-1">
                        <p className="tabular-nums">
                          {row.unloading.total_bags} bags · {fmtQtl(row.unloading.total_weight_qtl)} qtl
                        </p>
                        {(row.unloading.destinations || []).slice(0, 3).map((d, i) => (
                          <p key={i} className="text-[10px] text-muted-foreground">
                            {d.stack_id || d.destination || "Stack"}
                            {d.bags != null ? ` · ${d.bags} bags` : ""}
                            {d.weight_qtl != null ? ` · ${fmtQtl(d.weight_qtl)} qtl` : ""}
                          </p>
                        ))}
                        {(row.unloading.destinations || []).length > 3 && (
                          <p className="text-[10px] text-muted-foreground">
                            +{(row.unloading.destinations || []).length - 3} more
                          </p>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function SummaryCell({ label, value, highlight }) {
  return (
    <div className={`rounded-md border px-2.5 py-2 ${highlight ? "border-bansal-sage/40 bg-bansal-sage/5" : "border-border/60"}`}>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="mt-0.5 font-medium tabular-nums">{value}</p>
    </div>
  );
}
